/**
 * DeclarationModal — 4-step Pajemploi declaration flow (with proactive preparation).
 * Steps: Préparation → Estimation → Pré-décl. → Confirmation
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Send, Loader2, AlertTriangle, CheckCircle, XCircle, Baby, Calculator, FileCheck, ShieldCheck, UserCheck, ScrollText, Users
} from 'lucide-react';
import { WizardStepper } from '../WizardStepper';

const DECL_STEP_LABELS = ['Préparation', 'Estimation', 'Pré-décl.', 'Confirmation'];
const DECL_STEPS_ORDER = ['prepare', 'estimate', 'predeclare', 'confirm'];
const getDeclStepNum = (pajemploiStep) => {
  const idx = DECL_STEPS_ORDER.indexOf(pajemploiStep);
  return idx >= 0 ? idx + 1 : 1;
};

const STEP_ICONS = {
  verify_employer: ShieldCheck,
  verify_employee: UserCheck,
  register_mandate: ScrollText,
  associate_employee: Users,
  verify_children: Baby,
};

const PrepareStepLine = ({ step, isActive }) => {
  const Icon = STEP_ICONS[step.step] || ShieldCheck;
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`prepare-step-${step.step}`}>
      <div className="shrink-0">
        {step.success === true && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--success-light)]">
            <CheckCircle className="h-4 w-4 text-[var(--success)]" />
          </div>
        )}
        {step.success === false && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--error)]/10">
            <XCircle className="h-4 w-4 text-[var(--error)]" />
          </div>
        )}
        {step.success === undefined && isActive && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--primary-100)]">
            <Loader2 className="h-4 w-4 animate-spin text-[var(--primary-400)]" />
          </div>
        )}
        {step.success === undefined && !isActive && (
          <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[var(--neutral-100)]">
            <Icon className="h-4 w-4 text-[var(--text-muted)]" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" style={{ color: step.success === false ? 'var(--error)' : step.success === true ? 'var(--success)' : 'var(--text-primary)' }}>
          {step.label}
        </p>
        {step.message && step.success !== undefined && (
          <p className="text-xs mt-0.5" style={{ color: step.success === false ? 'var(--error)' : 'var(--text-secondary)' }}>
            {step.message}
          </p>
        )}
      </div>
    </div>
  );
};

export const DeclarationModal = ({
  open,
  onOpenChange,
  selectedDraft,
  pajemploiStep,
  // Preparation
  preparing,
  preparationSteps,
  preparationSuccess,
  // Old readiness (kept for compatibility)
  checkingReadiness,
  readiness,
  // Estimation
  estimation,
  // Predeclaration
  predeclaration,
  predeclarationFailed,
  estimating,
  predeclaring,
  submitting,
  onEstimate,
  onPredeclare,
  onSubmit,
  onSkipPredeclare,
  onGoToProfile,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg bg-[var(--bg-primary)] p-0 flex flex-col max-h-[85vh]" data-testid="declaration-modal" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="px-6 pt-6 pb-2">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--primary-700)]">
            <Send className="h-5 w-5" /> Déclarer à Pajemploi
          </DialogTitle>
          <DialogDescription>
            {pajemploiStep === 'prepare' && 'Préparation — Vérification et configuration Pajemploi'}
            {pajemploiStep === 'estimate' && 'Étape 2/4 — Estimation des cotisations'}
            {pajemploiStep === 'predeclare' && 'Étape 3/4 — Pré-déclaration & vérification'}
            {pajemploiStep === 'confirm' && 'Étape 4/4 — Confirmation finale'}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <WizardStepper
          totalSteps={4}
          currentStep={getDeclStepNum(pajemploiStep)}
          labels={DECL_STEP_LABELS}
        />
      </div>

      {selectedDraft && (
        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-4">

          {/* ══════════════════════════════════════════════
              STEP 1: PREPARATION (auto-cascade)
              ══════════════════════════════════════════════ */}
          {pajemploiStep === 'prepare' && (
            <div className="space-y-1">
              {/* Show preparation steps */}
              {preparationSteps.length > 0 ? (
                <div className="rounded-lg border border-[var(--neutral-200)] bg-[var(--neutral-50)] p-4">
                  {preparationSteps.map((step, idx) => (
                    <PrepareStepLine
                      key={step.step}
                      step={step}
                      isActive={preparing && idx === preparationSteps.length - 1 && step.success === undefined}
                    />
                  ))}
                  {preparing && (
                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[var(--neutral-200)]">
                      <Loader2 className="h-4 w-4 animate-spin text-[var(--primary-400)]" />
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Vérification en cours...</span>
                    </div>
                  )}
                </div>
              ) : preparing ? (
                <div className="flex items-center gap-2 p-4 rounded-lg bg-[var(--neutral-50)]">
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--primary-400)]" />
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Préparation Pajemploi en cours...</span>
                </div>
              ) : null}

              {/* Preparation failed */}
              {!preparing && preparationSteps.length > 0 && !preparationSuccess && (
                <Alert className="mt-3 border-[var(--error)]/30 bg-[var(--error)]/5">
                  <XCircle className="h-4 w-4 text-[var(--error)]" />
                  <AlertDescription>
                    <p className="font-semibold text-sm mb-1 text-[var(--error)]">Préparation Pajemploi échouée</p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      Corrigez les informations dans les profils (employeur et/ou salarié) puis réessayez.
                    </p>
                    <Button variant="outline" size="sm" className="mt-2 gap-1 text-xs border-[var(--primary-400)] text-[var(--primary-400)]" onClick={onGoToProfile} data-testid="go-to-profile-btn">
                      Compléter les profils
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Preparation success */}
              {!preparing && preparationSuccess && (
                <Alert className="mt-3 border-[var(--success)] bg-[var(--success-light)]">
                  <CheckCircle className="h-4 w-4 text-[var(--success)]" />
                  <AlertDescription className="text-xs text-[var(--success)]">
                    <p className="font-semibold mb-0.5">Pajemploi prêt !</p>
                    <p>Mandat actif, salarié associé. Vous pouvez lancer l'estimation.</p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Summary card (shown from step 2 onwards) */}
          {pajemploiStep !== 'prepare' && (
            <div className="p-4 rounded-lg bg-[var(--primary-50)] border border-[var(--primary-200)]">
              <div className="flex items-center gap-2 mb-3">
                <Baby className="h-5 w-5 text-[var(--primary-500)]" />
                <span className="font-semibold text-[var(--primary-700)] text-sm">{selectedDraft.child_name}</span>
                <span className="text-xs text-[var(--text-muted)]">—</span>
                <span className="text-xs text-[var(--text-secondary)]">
                  {new Date(selectedDraft.month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="flex justify-between"><span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Heures</span><span className="font-medium text-sm">{selectedDraft.hours_worked}h</span></div>
                <div className="flex justify-between"><span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Jours</span><span className="font-medium text-sm">{selectedDraft.days_worked}j</span></div>
                <div className="flex justify-between"><span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Brut</span><span className="font-medium text-sm">{selectedDraft.gross_salary.toFixed(2)}€</span></div>
                <div className="flex justify-between"><span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Net à payer</span><span className="font-bold text-sm" style={{ color: 'var(--success)' }}>{selectedDraft.net_salary.toFixed(2)}€</span></div>
              </div>
            </div>
          )}

          {/* Estimation results */}
          {estimation && pajemploiStep !== 'estimate' && (
            <div className="p-3 rounded-lg bg-[var(--info-light)] border border-[var(--info)]">
              <h4 className="text-xs font-semibold uppercase tracking-wide mb-2 text-[var(--info)]">
                <Calculator className="inline h-3.5 w-3.5 mr-1" /> Estimation {estimation.source === 'pajemploi' && estimation.success ? 'Pajemploi' : 'Nannyou (locale)'}
              </h4>
              {estimation.error && (
                <p className="text-xs mb-2 text-[var(--warning-dark)]">
                  <AlertTriangle className="inline h-3 w-3 mr-1" />
                  {estimation.error}
                </p>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span style={{ color: 'var(--text-secondary)' }}>Cotis. salariales</span>
                <span className="text-right font-medium">-{(estimation.local_data?.employee_contributions || selectedDraft.employee_contributions).toFixed(2)}€</span>
                <span style={{ color: 'var(--text-secondary)' }}>Cotis. patronales</span>
                <span className="text-right font-medium">{(estimation.local_data?.employer_contributions || selectedDraft.employer_contributions).toFixed(2)}€</span>
                {estimation.estimation?.mntCotiPriseEnChargeOdpf > 0 && (<>
                  <span style={{ color: 'var(--text-secondary)' }}>Prise en charge ODPF</span>
                  <span className="text-right font-medium text-[var(--success)]">{estimation.estimation.mntCotiPriseEnChargeOdpf.toFixed(2)}€</span>
                </>)}
              </div>
            </div>
          )}

          {/* Pre-declaration failed */}
          {predeclarationFailed && predeclaration?.error && pajemploiStep === 'predeclare' && (
            <Alert className="border-[var(--warning)] bg-[var(--warning-light)]">
              <AlertTriangle className="h-4 w-4 text-[var(--warning)]" />
              <AlertDescription className="text-xs text-[var(--warning-dark)]">
                <p className="font-semibold mb-1">Pré-déclaration Pajemploi non aboutie</p>
                {predeclaration.error_details?.errors?.length > 0 ? (
                  <div className="space-y-2">
                    {predeclaration.error_details.errors.map((err, i) => (
                      <div key={i} className="space-y-0.5">
                        <p className="font-medium">{err.message}</p>
                        {err.action && <p className="opacity-80 italic">{err.action}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>{predeclaration.error}</p>
                )}
                <p className="mt-2 opacity-70 text-[11px]">Vous pouvez enregistrer la déclaration localement et réessayer ultérieurement.</p>
              </AlertDescription>
            </Alert>
          )}

          {/* Pre-declaration success */}
          {predeclaration?.success && pajemploiStep === 'confirm' && (
            <Alert className="border-[var(--success)] bg-[var(--success-light)]">
              <CheckCircle className="h-4 w-4 text-[var(--success)]" />
              <AlertDescription className="text-xs text-[var(--success)]">
                <p className="font-semibold mb-1">Pré-déclaration validée par Pajemploi</p>
                <p>Les montants ont été vérifiés. Vous pouvez confirmer la déclaration finale.</p>
              </AlertDescription>
            </Alert>
          )}

          {/* Local-only confirmation (skipped predeclare) */}
          {!predeclaration && pajemploiStep === 'confirm' && (
            <Alert className="border-[var(--info)] bg-[var(--info-light)]">
              <Send className="h-4 w-4 text-[var(--info)]" />
              <AlertDescription className="text-xs text-[var(--info-dark)]">
                <p className="font-semibold mb-1">Déclaration locale</p>
                <p>La déclaration sera enregistrée dans Nannyou. Vous pourrez soumettre à Pajemploi ultérieurement.</p>
              </AlertDescription>
            </Alert>
          )}

          {/* Ready for estimation */}
          {pajemploiStep === 'estimate' && !estimation && (
            <Alert className="border-[var(--success)] bg-[var(--success-light)]">
              <CheckCircle className="h-4 w-4 text-[var(--success)]" />
              <AlertDescription className="text-xs text-[var(--success)]">Pajemploi configuré. Lancez l'estimation pour calculer les cotisations officielles.</AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Sticky footer */}
      <div className="border-t border-[var(--neutral-200)] px-6 py-4 bg-[var(--bg-primary)]" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting || preparing} data-testid="cancel-declaration-btn" className="sm:w-auto">Fermer</Button>
          <div className="flex justify-end">
            {/* Prepare step — no action button needed (auto-cascade), but show retry if failed */}
            {pajemploiStep === 'prepare' && !preparing && !preparationSuccess && preparationSteps.length > 0 && (
              <Button variant="outline" onClick={onGoToProfile} className="gap-2 w-full sm:w-auto" data-testid="prepare-fix-btn">
                Compléter les profils
              </Button>
            )}
            {/* Prepare step — if success, advance to estimate */}
            {pajemploiStep === 'prepare' && !preparing && preparationSuccess && (
              <Button onClick={onEstimate} className="gap-2 w-full sm:w-auto" style={{ backgroundColor: 'var(--primary-400)', color: 'white' }} data-testid="start-estimate-btn">
                <Calculator className="h-4 w-4" /> Lancer l'estimation
              </Button>
            )}
            {pajemploiStep === 'estimate' && (
              <Button onClick={onEstimate} disabled={estimating} className="gap-2 w-full sm:w-auto" style={{ backgroundColor: 'var(--primary-400)', color: 'white' }} data-testid="estimate-btn">
                {estimating ? (<><Loader2 className="h-4 w-4 animate-spin" /> Estimation...</>) : (<><Calculator className="h-4 w-4" /> Estimer les cotisations</>)}
              </Button>
            )}
            {pajemploiStep === 'predeclare' && !predeclarationFailed && (
              <Button onClick={onPredeclare} disabled={predeclaring} className="gap-2 w-full sm:w-auto" style={{ backgroundColor: 'var(--primary-400)', color: 'white' }} data-testid="predeclare-btn">
                {predeclaring ? (<><Loader2 className="h-4 w-4 animate-spin" /> Pré-déclaration...</>) : (<><FileCheck className="h-4 w-4" /> Pré-déclarer via Pajemploi</>)}
              </Button>
            )}
            {pajemploiStep === 'predeclare' && predeclarationFailed && (
              <Button onClick={onSkipPredeclare} className="gap-2 w-full sm:w-auto" style={{ backgroundColor: 'var(--primary-400)', color: 'white' }} data-testid="skip-predeclare-btn">
                <Send className="h-4 w-4" /> Déclarer localement
              </Button>
            )}
            {pajemploiStep === 'confirm' && (
              <Button onClick={onSubmit} disabled={submitting} className="gap-2 w-full sm:w-auto" style={{ backgroundColor: 'var(--success)', color: 'white' }} data-testid="confirm-declaration-btn">
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours...</>) : (<><Send className="h-4 w-4" /> Confirmer la déclaration</>)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
